<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import AppDataTable from "@/components/AppDataTable.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useResourceList } from "@/composables/useResourceList";
import { formatDate } from "@/lib/date";
import { fetchPosts, fetchPost, editPost, deletePost, setPostRecommended } from "./api";
import { uploadImage, uploadVideo } from "@/lib/uploads";
import RichTextField from "@/components/RichTextField.vue";
import HeicImg from "@/components/HeicImg.vue";

const router = useRouter();
const columns = [
  { key: "author",      label: "Author",   sortable: true, width: "160px" },
  { key: "content",     label: "Content",  width: "220px" },
  { key: "media",       label: "Media",    align: "center", width: "180px" },
  { key: "likes",       label: "Likes",    align: "center", sortable: true },
  { key: "comments",    label: "Comments", align: "center", sortable: true },
  { key: "recommended", label: "Starred",  align: "center", width: "80px" },
  { key: "actions",     label: "Actions",  align: "end", width: "180px" },
];
const { rows, loading, error, pendingDelete, deleting, load, askDelete, confirmDelete } =
  useResourceList({
    fetch: fetchPosts,
    remove: deletePost,
    mapRow: (p) => ({
      id:          p.id ?? p._id ?? p.objectID,
      docId:       p.objectID ?? p.id ?? p._id,
      author:      p.author?.name ?? p.author ?? "—",
      content:     (p.content ?? "").slice(0, 60),
      postImages:  Array.isArray(p.postImage) ? p.postImage.slice(0, 3) : (p.postImage ? [p.postImage] : []),
      postVideo:   typeof p.postVideo === "string" && p.postVideo ? p.postVideo : (Array.isArray(p.postVideo) && p.postVideo[0] ? p.postVideo[0] : ""),
      likes:       p.likes?.length || 0,
      comments:    p.comments?.length || 0,
      recommended: !!p.recommended,
      createdAt:   formatDate(p.createdAt),
    }),
  });

const togglingId = ref(null);
async function toggleRecommended(row) {
  const next = !row.recommended;
  togglingId.value = row.id;
  row.recommended = next; // optimistic
  try {
    await setPostRecommended(row.docId, next);
  } catch {
    row.recommended = !next; // revert
    error.value = "Couldn't update the starred status. Please try again.";
  } finally {
    togglingId.value = null;
  }
}

// ── Preview post modal ──────────────────────────────────────────────────────
const previewDialog = ref(false);
const previewLoading = ref(false);
const previewPost = ref(null);
const previewExpanded = ref(false);
const PREVIEW_TRUNCATE = 160;

async function openPreviewModal(row) {
  previewPost.value = null; previewExpanded.value = false;
  previewDialog.value = true; previewLoading.value = true;
  try {
    const post = await fetchPost(row.docId ?? row.id);
    previewPost.value = {
      author:     post?.author?.name ?? post?.author ?? row.author ?? "—",
      avatar:     post?.author?.photoUrl ?? post?.author?.avatar ?? null,
      content:    post?.content ?? row.content ?? "",
      images:     Array.isArray(post?.postImage) ? post.postImage : (post?.postImage ? [post.postImage] : []),
      video:      typeof post?.postVideo === "string" && post.postVideo ? post.postVideo : (Array.isArray(post?.postVideo) && post.postVideo[0] ? post.postVideo[0] : ""),
      createdAt:  row.createdAt,
    };
  } catch { previewPost.value = { author: row.author, content: row.content, images: row.postImages, video: row.postVideo, createdAt: row.createdAt, avatar: null }; }
  finally { previewLoading.value = false; }
}

// ── Edit post modal ──────────────────────────────────────────────────────────
const editDialog = ref(false);
const editLoading = ref(false);
const editSaving = ref(false);
const editError = ref("");
const editRow = ref(null);
const editContent = ref("");
const editImages = ref([]);   // hosted URLs
const editVideo = ref("");
const editUploadingMedia = ref(false);

async function openEditModal(row) {
  editError.value = ""; editContent.value = ""; editImages.value = []; editVideo.value = ""; editRow.value = row;
  editDialog.value = true; editLoading.value = true;
  try {
    const post = await fetchPost(row.docId ?? row.id);
    editContent.value = post?.content ?? row.content ?? "";
    editImages.value = Array.isArray(post?.postImage) ? [...post.postImage] : post?.postImage ? [post.postImage] : [];
    const vid = post?.postVideo;
    editVideo.value = typeof vid === "string" && vid ? vid : (Array.isArray(vid) && vid[0] ? vid[0] : "");
  } catch { editError.value = "Couldn't load the post."; }
  finally { editLoading.value = false; }
}

async function addEditPhotos(e) {
  const files = Array.from(e.target.files || []); e.target.value = "";
  editUploadingMedia.value = true;
  try { for (const file of files) { if (file.size / (1024 * 1024) > 500) { alert("File too large (max 500 MB)."); continue; } editImages.value.push(await uploadImage("posts", file)); } }
  catch { editError.value = "Couldn't upload image."; }
  finally { editUploadingMedia.value = false; }
}

async function addEditVideo(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  editUploadingMedia.value = true;
  try { editVideo.value = await uploadVideo(file); }
  catch { editError.value = "Couldn't upload video."; }
  finally { editUploadingMedia.value = false; }
}

async function saveEditPost() {
  editSaving.value = true; editError.value = "";
  try {
    await editPost(editRow.value.docId ?? editRow.value.id, { content: editContent.value, postImage: editImages.value, postVideo: editVideo.value });
    editRow.value.content = editContent.value.slice(0, 80);
    editDialog.value = false;
  } catch { editError.value = "Couldn't save the post. Please try again."; }
  finally { editSaving.value = false; }
}
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Manage Posts</h1>
      <div class="d-flex gap-2">
        <VBtn variant="tonal" @click="load">Refresh</VBtn>
        <VBtn variant="tonal" @click="router.push({ name: 'posts-export' })">Export</VBtn>
        <VBtn @click="router.push({ name: 'posts-create' })">Create Post</VBtn>
      </div>
    </div>
    <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
    <AppDataTable :columns="columns" :rows="rows" :loading="loading" row-key="id" searchable search-placeholder="Search posts…" empty-text="No posts yet.">
      <template #cell-media="{ row }">
        <div class="d-flex align-center gap-1 flex-wrap">
          <HeicImg v-for="(src, i) in row.postImages" :key="i" :src="src" style="height:40px;width:40px;object-fit:cover;border-radius:4px" alt="" />
          <video v-if="row.postVideo" :src="row.postVideo" style="height:40px;width:60px;object-fit:cover;border-radius:4px" />
          <span v-if="!row.postImages.length && !row.postVideo" class="text-medium-emphasis text-caption">—</span>
        </div>
      </template>
      <template #cell-recommended="{ row }">
        <VBtn
          :color="row.recommended ? 'warning' : 'default'"
          :loading="togglingId === row.id"
          size="small"
          variant="text"
          icon
          :title="row.recommended ? 'Un-star post' : 'Star post'"
          @click.stop="toggleRecommended(row)"
        >
          <VIcon :color="row.recommended ? 'warning' : undefined">{{ row.recommended ? 'ri-star-fill' : 'ri-star-line' }}</VIcon>
        </VBtn>
      </template>
      <template #cell-actions="{ row }">
        <VBtn size="small" variant="text" color="primary" @click.stop="openPreviewModal(row)">Preview</VBtn>
        <VBtn size="small" variant="text" @click.stop="openEditModal(row)">Edit</VBtn>
        <VBtn size="small" color="error" variant="text" @click.stop="askDelete(row)">Delete</VBtn>
      </template>
    </AppDataTable>
    <ConfirmDialog :model-value="pendingDelete !== null" title="Delete post?" message="Permanently delete this post?" :loading="deleting" @update:model-value="pendingDelete = null" @confirm="confirmDelete" />

    <!-- Preview Post Modal -->
    <VDialog v-model="previewDialog" max-width="400">
      <VCard style="overflow:hidden; border-radius:16px">
        <!-- App bar -->
        <div style="background:#7367f0; padding:12px 16px; display:flex; align-items:center; gap:10px">
          <div style="width:36px;height:36px;border-radius:50%;background:white;display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0">
            <VIcon color="primary" size="22">ri-team-fill</VIcon>
          </div>
          <span style="color:white;font-weight:600;font-size:15px">Brilliant Aspialites</span>
          <VIcon color="warning" size="18" class="ml-1">ri-star-fill</VIcon>
          <VSpacer />
          <VBtn icon size="x-small" variant="text" color="white" @click="previewDialog = false">
            <VIcon size="18">ri-close-line</VIcon>
          </VBtn>
        </div>

        <div style="max-height:600px;overflow-y:auto;background:#f5f5f5">
          <p v-if="previewLoading" class="pa-6 text-center text-medium-emphasis">Loading…</p>
          <template v-else-if="previewPost">
            <!-- Post card -->
            <div style="background:white;margin:0">
              <!-- Author row -->
              <div style="padding:12px 14px 8px;display:flex;align-items:center;gap:10px">
                <div style="width:42px;height:42px;border-radius:50%;background:#7367f0;display:flex;align-items:center;justify-content:center;flex-shrink:0;overflow:hidden">
                  <img v-if="previewPost.avatar" :src="previewPost.avatar" style="width:100%;height:100%;object-fit:cover" />
                  <VIcon v-else color="white" size="20">ri-user-fill</VIcon>
                </div>
                <div>
                  <div style="font-weight:600;font-size:14px;line-height:1.2">{{ previewPost.author }}</div>
                  <div style="font-size:12px;color:#888">{{ previewPost.createdAt }}</div>
                </div>
              </div>

              <!-- Content -->
              <div style="padding:0 14px 10px;font-size:14px;line-height:1.55;color:#222">
                <template v-if="!previewExpanded && previewPost.content.length > PREVIEW_TRUNCATE">
                  <span v-html="previewPost.content.slice(0, PREVIEW_TRUNCATE)" /><span>… </span>
                  <span style="color:#7367f0;cursor:pointer;font-size:13px" @click="previewExpanded = true">read more...</span>
                </template>
                <span v-else v-html="previewPost.content" />
              </div>

              <!-- Images -->
              <template v-if="previewPost.images?.length">
                <HeicImg v-for="(src, i) in previewPost.images" :key="i" :src="src" style="width:100%;display:block;max-height:320px;object-fit:cover" alt="" />
              </template>

              <!-- Video -->
              <video v-if="previewPost.video" :src="previewPost.video" controls style="width:100%;display:block;max-height:240px;background:#000" />
            </div>
          </template>
        </div>
      </VCard>
    </VDialog>

    <!-- Edit Post Modal -->
    <VDialog v-model="editDialog" max-width="560">
      <VCard title="Edit Post">
        <VCardText>
          <p v-if="editLoading" class="text-medium-emphasis">Loading post…</p>
          <template v-else>
            <VAlert v-if="editError" type="error" class="mb-4">{{ editError }}</VAlert>
            <RichTextField v-model="editContent" label="What's on your mind?" class="mb-3" />

            <!-- Image previews -->
            <div v-if="editImages.length" class="d-flex flex-wrap gap-2 mb-3">
              <div v-for="(src, i) in editImages" :key="i" style="position:relative; display:inline-block">
                <img :src="src" style="max-height:100px; border-radius:6px" alt="" />
                <VBtn
                  icon size="x-small" color="error"
                  style="position:absolute; top:2px; right:2px; opacity:0.85"
                  @click="editImages.splice(i, 1)"
                ><VIcon size="14">ri-close-line</VIcon></VBtn>
              </div>
            </div>

            <!-- Video preview -->
            <div v-if="editVideo" class="mb-3 d-flex align-center gap-2">
              <video :src="editVideo" controls style="max-height:120px; border-radius:6px" />
              <VBtn variant="text" size="small" color="error" @click="editVideo = ''">Remove</VBtn>
            </div>

            <!-- Media upload -->
            <div class="d-flex gap-3 flex-wrap align-center">
              <label class="edit-media-btn">
                📷 Add Photo
                <input type="file" multiple accept="image/*" class="d-none" @change="addEditPhotos" />
              </label>
              <label class="edit-media-btn">
                🎬 Add Video
                <input type="file" accept="video/*" class="d-none" @change="addEditVideo" />
              </label>
              <span v-if="editUploadingMedia" class="text-caption text-medium-emphasis">Uploading…</span>
            </div>
          </template>
        </VCardText>
        <VCardActions class="px-4 pb-4 justify-end gap-2">
          <VBtn variant="tonal" @click="editDialog = false">Cancel</VBtn>
          <VBtn :loading="editSaving" :disabled="editLoading || editUploadingMedia" @click="saveEditPost">Save</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style scoped>
.edit-media-btn {
  display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
  padding: 6px 12px; border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px; font-size: 0.875rem;
}
.edit-media-btn:hover { background: rgba(var(--v-border-color), 0.1); }
</style>
